import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DeleteOutline,
  ExpandMore,
  Search,
  WarningAmber,
} from "@mui/icons-material";
import { collection, deleteDoc, doc, getDocs, query } from "firebase/firestore";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import * as XLSX from "xlsx";
import useAdminAccess from "../../../hooks/use_admin_access";
import AdminDashboardLayout from "../../../layouts/admin_dashboard_layout/admin_dashboard_layout";
import { useFeedback } from "../../../providers/feedback/feedback";
import {
  MAIN_THEME_COLOR,
  SECONDARY_THEME_COLOR,
} from "../../../providers/theme/colors/colors";

type RegistrationRecord = {
  id?: string;
  deanery?: { id?: number; name?: string };
  parish?: { id?: number; value?: string; name?: string };
  presidentName?: string;
  animatorName?: string;
  presidentContactNumber?: string;
  animatorContactNumber?: string;
  contactNumber?: string;
  email?: string;
  totalParticipantsCount?: number | string;
  selectedEvents?: string[];
  createdAt?: string;
  isPitchBattleEntry?: boolean;
  events?: {
    spotlight?: {
      performingEventName?: string;
      entryName?: string;
      eventDescription?: string;
      participantCount?: number;
      participants?: string[];
      teamLeadContact?: string;
    };
    jam?: {
      participantCount?: number;
      participants?: string[];
      participantContact?: string;
    };
    ecomosaic?: {
      participantCount?: number;
      participants?: string[];
      contacts?: string[];
    };
    faithvault?: {
      participantCount?: number;
      participants?: string[];
      teamLeadContact?: string;
    };
    pitchbattle?: {
      entryType?: "individual" | "group";
      ideaTitle?: string;
      participantCount?: number;
      participants?: string[];
      teamLeadContact?: string;
    };
  };
};

type PitchBattleRecord = {
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  deanery?: { id?: number; name?: string };
  parish?: { id?: number; value?: string; name?: string };
  presidentName?: string;
  animatorName?: string;
  presidentContactNumber?: string;
  animatorContactNumber?: string;
  contactNumber?: string;
  email?: string;
  entryType?: "individual" | "group";
  participantCount?: number;
  participantNames?: string[];
  teamLeadContact?: string;
  ideaTitle?: string;
};

type ParishGroup = {
  parishKey: string;
  parishName: string;
  deaneryName: string;
  totalParticipantsCount: number;
  mainRecords: RegistrationRecord[];
  pitchBattleRecords: PitchBattleRecord[];
};

const eventLabels: Record<string, string> = {
  spotlight: "SpotLight",
  jam: "JAM (Just A Minute)",
  ecomosaic: "Eco - Mosaic",
  faithvault: "The Faith Vault",
  pitchbattle: "Pitch Battle",
};

const ExcelIcon = ({ bg = "#107C41" }: { bg?: string }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 20,
      height: 20,
      borderRadius: "4px",
      backgroundColor: bg,
      color: "#ffffff",
      fontWeight: 900,
      fontSize: "11px",
      fontFamily: "Segoe UI, sans-serif",
      boxShadow: "0 2px 4px rgba(0,0,0,0.18)",
      mr: 1,
      flexShrink: 0,
    }}
  >
    X
  </Box>
);

function getParticipantCountFromRecord(record: RegistrationRecord): number {
  if (typeof record.totalParticipantsCount === "number" && !isNaN(record.totalParticipantsCount)) {
    return record.totalParticipantsCount;
  }
  if (typeof record.totalParticipantsCount === "string") {
    const parsed = parseInt(record.totalParticipantsCount, 10);
    if (!isNaN(parsed)) return parsed;
  }
  let count = 0;
  if (record.events?.spotlight?.participantCount) count += record.events.spotlight.participantCount;
  if (record.events?.jam?.participantCount) count += record.events.jam.participantCount;
  if (record.events?.ecomosaic?.participantCount) count += record.events.ecomosaic.participantCount;
  if (record.events?.faithvault?.participantCount) count += record.events.faithvault.participantCount;
  if (record.events?.pitchbattle?.participantCount) count += record.events.pitchbattle.participantCount;
  return count;
}

export default function DycAdminPage() {
  const firestore = useFirestore();
  const { showSnackbar } = useFeedback();
  const { isSuperAdmin } = useAdminAccess();

  const [searchQuery, setSearchQuery] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    label: string;
    collectionName: "dyc_yuva_dabazo" | "dyc_pitch_battle" | "both";
  } | null>(null);
  const [parishDeleteTarget, setParishDeleteTarget] = useState<ParishGroup | null>(null);
  const [showClearAllDialog, setShowClearAllDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const mainRegistrationQuery = query(collection(firestore, "dyc_yuva_dabazo"));
  const { data: mainRegistrations } = useFirestoreCollectionData<RegistrationRecord>(
    mainRegistrationQuery,
    { idField: "id" },
  );

  const pitchBattleQuery = query(collection(firestore, "dyc_pitch_battle"));
  const { data: pitchBattleRegistrations } = useFirestoreCollectionData<PitchBattleRecord>(
    pitchBattleQuery,
    { idField: "id" },
  );

  const mainOnlyRegistrations = useMemo(
    () => (mainRegistrations || []).filter((r) => !r.isPitchBattleEntry),
    [mainRegistrations],
  );

  const combinedPitchBattleRecords = useMemo(() => {
    const list: PitchBattleRecord[] = [...(pitchBattleRegistrations || [])];
    const existingIds = new Set(list.map((item) => item.id).filter(Boolean));

    (mainRegistrations || []).forEach((record) => {
      if (record.events?.pitchbattle) {
        const pb = record.events.pitchbattle;
        const key = record.id || `${record.email}-${pb.ideaTitle}`;
        if (!existingIds.has(key)) {
          existingIds.add(key);
          list.push({
            id: record.id,
            createdAt: record.createdAt,
            deanery: record.deanery,
            parish: record.parish,
            presidentName: record.presidentName,
            animatorName: record.animatorName,
            presidentContactNumber: record.presidentContactNumber || record.contactNumber,
            animatorContactNumber: record.animatorContactNumber,
            email: record.email,
            entryType: pb.entryType || "individual",
            participantCount: pb.participantCount || pb.participants?.length || 1,
            participantNames: pb.participants || [],
            teamLeadContact: pb.teamLeadContact || record.contactNumber || "",
            ideaTitle: pb.ideaTitle || "Pitch Battle",
          });
        }
      }
    });

    return list;
  }, [mainRegistrations, pitchBattleRegistrations]);

  const parishGroups = useMemo<ParishGroup[]>(() => {
    const map = new Map<string, ParishGroup>();

    mainOnlyRegistrations.forEach((record) => {
      const parishName = record.parish?.value || record.parish?.name || "-";
      const deaneryName = record.deanery?.name || "-";
      const parishKey = `${deaneryName}::${parishName}`;

      const existing = map.get(parishKey) || {
        parishKey,
        parishName,
        deaneryName,
        totalParticipantsCount: 0,
        mainRecords: [],
        pitchBattleRecords: [],
      };

      existing.mainRecords.push(record);
      existing.totalParticipantsCount += getParticipantCountFromRecord(record);
      map.set(parishKey, existing);
    });

    combinedPitchBattleRecords.forEach((pbRecord) => {
      const parishName = pbRecord.parish?.value || pbRecord.parish?.name || "-";
      const deaneryName = pbRecord.deanery?.name || "-";
      const parishKey = `${deaneryName}::${parishName}`;

      const existing = map.get(parishKey) || {
        parishKey,
        parishName,
        deaneryName,
        totalParticipantsCount: 0,
        mainRecords: [],
        pitchBattleRecords: [],
      };

      existing.pitchBattleRecords.push(pbRecord);
      map.set(parishKey, existing);
    });

    const groups = Array.from(map.values()).sort((a, b) =>
      `${a.deaneryName}-${a.parishName}`.localeCompare(`${b.deaneryName}-${b.parishName}`),
    );

    if (!searchQuery.trim()) {
      return groups;
    }

    const q = searchQuery.toLowerCase().trim();
    return groups.filter(
      (g) =>
        g.parishName.toLowerCase().includes(q) ||
        g.deaneryName.toLowerCase().includes(q) ||
        g.mainRecords.some((r) => r.presidentName?.toLowerCase().includes(q)) ||
        g.pitchBattleRecords.some((pb) => pb.ideaTitle?.toLowerCase().includes(q)),
    );
  }, [mainOnlyRegistrations, combinedPitchBattleRecords, searchQuery]);

  const handleDeleteSingleRecord = async () => {
    if (!deleteTarget || !deleteTarget.id) return;
    setIsDeleting(true);

    try {
      if (deleteTarget.collectionName === "dyc_yuva_dabazo" || deleteTarget.collectionName === "both") {
        await deleteDoc(doc(firestore, "dyc_yuva_dabazo", deleteTarget.id));
      }
      if (deleteTarget.collectionName === "dyc_pitch_battle" || deleteTarget.collectionName === "both") {
        await deleteDoc(doc(firestore, "dyc_pitch_battle", deleteTarget.id));
      }

      showSnackbar(`Record for "${deleteTarget.label}" deleted successfully.`);
    } catch (error) {
      showSnackbar("Failed to delete record.", "#f44336");
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleDeleteParishGroup = async () => {
    if (!parishDeleteTarget) return;
    setIsDeleting(true);

    try {
      const deletePromises: Promise<void>[] = [];

      parishDeleteTarget.mainRecords.forEach((r) => {
        if (r.id) {
          deletePromises.push(deleteDoc(doc(firestore, "dyc_yuva_dabazo", r.id)));
        }
      });

      parishDeleteTarget.pitchBattleRecords.forEach((pb) => {
        if (pb.id) {
          deletePromises.push(deleteDoc(doc(firestore, "dyc_pitch_battle", pb.id)));
          deletePromises.push(deleteDoc(doc(firestore, "dyc_yuva_dabazo", pb.id)));
        }
      });

      await Promise.all(deletePromises);
      showSnackbar(`All entries for ${parishDeleteTarget.parishName} deleted successfully.`);
    } catch (error) {
      showSnackbar("Failed to delete parish entries.", "#f44336");
    } finally {
      setIsDeleting(false);
      setParishDeleteTarget(null);
    }
  };

  const handleClearAllData = async () => {
    setIsDeleting(true);
    try {
      const dycQuery = query(collection(firestore, "dyc_yuva_dabazo"));
      const dycSnapshot = await getDocs(dycQuery);
      const dycDeletes = dycSnapshot.docs.map((docSnap) => deleteDoc(docSnap.ref));

      const pbQuery = query(collection(firestore, "dyc_pitch_battle"));
      const pbSnapshot = await getDocs(pbQuery);
      const pbDeletes = pbSnapshot.docs.map((docSnap) => deleteDoc(docSnap.ref));

      await Promise.all([...dycDeletes, ...pbDeletes]);
      showSnackbar("All DYC registration data cleared successfully.");
    } catch (error) {
      showSnackbar("Failed to clear all registration data.", "#f44336");
    } finally {
      setIsDeleting(false);
      setShowClearAllDialog(false);
    }
  };

  const handleExportMainExcel = () => {
    const workbook = XLSX.utils.book_new();

    // Sheet 1: Parish Summary
    const summarySheetData = parishGroups.map((group, index) => ({
      "Sl. No": index + 1,
      Deanery: group.deaneryName,
      Parish: group.parishName,
      "Main Registrations": group.mainRecords.length,
      "Pitch Battle Entries": group.pitchBattleRecords.length,
      "Total Participants Count": group.totalParticipantsCount,
    }));
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(summarySheetData),
      "Parish Summary",
    );

    // Sheet 2: Main Registrations (with tick mark columns)
    const mainListSheetData = mainOnlyRegistrations.map((record, index) => {
      const selected = record.selectedEvents || [];
      return {
        "Sl. No": index + 1,
        Deanery: record.deanery?.name || "-",
        Parish: record.parish?.value || record.parish?.name || "-",
        "President Name": record.presidentName || "-",
        "President Contact": record.presidentContactNumber || record.contactNumber || "-",
        "Animator Name": record.animatorName || "-",
        "Animator Contact": record.animatorContactNumber || "-",
        Email: record.email || "-",
        "Total Youth Participants Count": getParticipantCountFromRecord(record),
        "SpotLight": selected.includes("spotlight") ? "✓" : "✗",
        "JAM (Just A Minute)": selected.includes("jam") ? "✓" : "✗",
        "Eco - Mosaic": selected.includes("ecomosaic") ? "✓" : "✗",
        "The Faith Vault": selected.includes("faithvault") ? "✓" : "✗",
        "Pitch Battle": selected.includes("pitchbattle") ? "✓" : "✗",
        "Registration Date": record.createdAt || "-",
      };
    });
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(mainListSheetData),
      "Main Registrations",
    );

    // Sheet 3: SpotLight Detailed Sheet
    const spotlightRows = mainOnlyRegistrations
      .filter((record) => record.selectedEvents?.includes("spotlight"))
      .map((record, index) => {
        const spotlight = record.events?.spotlight;
        return {
          "Sl. No": index + 1,
          Deanery: record.deanery?.name || "-",
          Parish: record.parish?.value || record.parish?.name || "-",
          "Event Name": spotlight?.performingEventName || spotlight?.entryName || "-",
          "Event Description": spotlight?.eventDescription || "-",
          "Participant Count": spotlight?.participantCount || (spotlight?.participants || []).length || 1,
          "Participant Names": (spotlight?.participants || []).join(", "),
          "Team Lead Contact": spotlight?.teamLeadContact || record.contactNumber || "-",
          "President Name": record.presidentName || "-",
          "Animator Name": record.animatorName || "-",
          Email: record.email || "-",
          "Registration Date": record.createdAt || "-",
        };
      });
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(spotlightRows),
      "SpotLight",
    );

    // Sheet 4: JAM Detailed Sheet
    const jamRows = mainOnlyRegistrations
      .filter((record) => record.selectedEvents?.includes("jam"))
      .map((record, index) => {
        const jam = record.events?.jam;
        return {
          "Sl. No": index + 1,
          Deanery: record.deanery?.name || "-",
          Parish: record.parish?.value || record.parish?.name || "-",
          "Participant Name": (jam?.participants || [])[0] || "-",
          "Contact Number": jam?.participantContact || record.contactNumber || "-",
          "President Name": record.presidentName || "-",
          "Animator Name": record.animatorName || "-",
          Email: record.email || "-",
          "Registration Date": record.createdAt || "-",
        };
      });
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(jamRows),
      "JAM",
    );

    // Sheet 5: Eco - Mosaic Detailed Sheet
    const ecomosaicRows = mainOnlyRegistrations
      .filter((record) => record.selectedEvents?.includes("ecomosaic"))
      .map((record, index) => {
        const eco = record.events?.ecomosaic;
        const participants = eco?.participants || [];
        const contacts = eco?.contacts || [];
        return {
          "Sl. No": index + 1,
          Deanery: record.deanery?.name || "-",
          Parish: record.parish?.value || record.parish?.name || "-",
          "Participant 1 Name": participants[0] || "-",
          "Participant 1 Contact": contacts[0] || record.contactNumber || "-",
          "Has Second Participant": participants[1] ? "Yes" : "No",
          "Participant 2 Name": participants[1] || "-",
          "Participant 2 Contact": contacts[1] || "-",
          "President Name": record.presidentName || "-",
          "Animator Name": record.animatorName || "-",
          Email: record.email || "-",
          "Registration Date": record.createdAt || "-",
        };
      });
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(ecomosaicRows),
      "Eco-Mosaic",
    );

    // Sheet 6: The Faith Vault Detailed Sheet
    const faithvaultRows = mainOnlyRegistrations
      .filter((record) => record.selectedEvents?.includes("faithvault"))
      .map((record, index) => {
        const faith = record.events?.faithvault;
        const members = faith?.participants || [];
        return {
          "Sl. No": index + 1,
          Deanery: record.deanery?.name || "-",
          Parish: record.parish?.value || record.parish?.name || "-",
          "Member 1 Name": members[0] || "-",
          "Member 2 Name": members[1] || "-",
          "Member 3 Name": members[2] || "-",
          "Team Lead Contact": faith?.teamLeadContact || record.contactNumber || "-",
          "President Name": record.presidentName || "-",
          "Animator Name": record.animatorName || "-",
          Email: record.email || "-",
          "Registration Date": record.createdAt || "-",
        };
      });
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(faithvaultRows),
      "The Faith Vault",
    );

    // Sheet 7: Pitch Battle Detailed Sheet
    const pitchBattleRows = combinedPitchBattleRecords.map((record, index) => ({
      "Sl. No": index + 1,
      Deanery: record.deanery?.name || "-",
      Parish: record.parish?.value || record.parish?.name || "-",
      "Pitch Title": record.ideaTitle || "-",
      "Entry Type": record.entryType || "individual",
      "Participant Count": record.participantCount || 1,
      "Participant Names": (record.participantNames || []).join(", "),
      "Team Lead Contact": record.teamLeadContact || "-",
      President: record.presidentName || "-",
      Animator: record.animatorName || "-",
      "President Contact": record.presidentContactNumber || record.contactNumber || "-",
      "Animator Contact": record.animatorContactNumber || "-",
      Email: record.email || "-",
      Date: record.createdAt || "-",
    }));
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(pitchBattleRows),
      "Pitch Battle",
    );

    XLSX.writeFile(workbook, "dyc-admin-registrations.xlsx");
  };

  return (
    <AdminDashboardLayout>
      <Box flex={1} mt={2} mx={{ xs: 1, md: 3 }} mb={4}>
        <Stack spacing={3}>
          <Card elevation={1} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={2}
              >
                <Box>
                  <Typography variant="h5" fontWeight={800} color={MAIN_THEME_COLOR}>
                    DYC Registrations Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    Parish-wise entries, main unit details, pitch battle registrations, and record deletion.
                  </Typography>
                </Box>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  {isSuperAdmin ? (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteOutline />}
                      onClick={() => setShowClearAllDialog(true)}
                      sx={{ borderRadius: 999, fontWeight: 700 }}
                    >
                      Clear All Data
                    </Button>
                  ) : null}

                  <Button
                    variant="contained"
                    onClick={handleExportMainExcel}
                    sx={{
                      borderRadius: 999,
                      backgroundColor: "#107C41",
                      color: "#ffffff",
                      fontWeight: 800,
                      "&:hover": { backgroundColor: "#0b5c30" },
                    }}
                  >
                    <ExcelIcon bg="#0b5c30" />
                    Export Excel
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <TextField
            fullWidth
            placeholder="Search by Parish, Deanery, President Name, or Pitch Title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ backgroundColor: "#ffffff", borderRadius: 2 }}
          />

          <Typography variant="subtitle1" fontWeight={800} color={MAIN_THEME_COLOR}>
            Registered Parishes ({parishGroups.length})
          </Typography>

          {parishGroups.length ? (
            parishGroups.map((group) => (
              <Accordion
                key={group.parishKey}
                sx={{
                  borderRadius: "12px !important",
                  border: `1px solid ${MAIN_THEME_COLOR}22`,
                  overflow: "hidden",
                  mb: 1.5,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    backgroundColor: "#f8fbff",
                    px: 2.5,
                    py: 1,
                  }}
                >
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="h6" fontWeight={800} color={MAIN_THEME_COLOR}>
                        {group.parishName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Deanery: {group.deaneryName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                        <Chip
                          label={`Participants: ${group.totalParticipantsCount}`}
                          size="small"
                          color="primary"
                        />
                        <Chip
                          label={`Main Reg: ${group.mainRecords.length}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`Pitch Battle: ${group.pitchBattleRecords.length}`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </Stack>
                    </Grid>
                    {isSuperAdmin ? (
                      <Grid item xs={12} sm={3} textAlign="right">
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutline />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setParishDeleteTarget(group);
                          }}
                        >
                          Delete Parish Data
                        </Button>
                      </Grid>
                    ) : null}
                  </Grid>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 2.5, backgroundColor: "#ffffff" }}>
                  <Stack spacing={2.5}>
                    {/* Main Registration Details */}
                    {group.mainRecords.length ? (
                      <Box>
                        <Typography variant="subtitle2" fontWeight={800} color={MAIN_THEME_COLOR} mb={1}>
                          Main Registration Entry
                        </Typography>
                        {group.mainRecords.map((mainRec) => (
                          <Card
                            key={mainRec.id || "main"}
                            variant="outlined"
                            sx={{ borderRadius: 2, mb: 1.5, p: 2, backgroundColor: "#fafcff" }}
                          >
                            <Grid container spacing={2} alignItems="flex-start">
                              <Grid item xs={12} sm={4}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  President & Contact
                                </Typography>
                                <Typography variant="subtitle2" fontWeight={800}>
                                  {mainRec.presidentName || "-"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Ph: {mainRec.presidentContactNumber || mainRec.contactNumber || "-"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Email: {mainRec.email || "-"}
                                </Typography>
                              </Grid>

                              <Grid item xs={12} sm={4}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Animator Details
                                </Typography>
                                <Typography variant="subtitle2" fontWeight={800}>
                                  {mainRec.animatorName || "-"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Ph: {mainRec.animatorContactNumber || "-"}
                                </Typography>
                              </Grid>

                              <Grid item xs={12} sm={3}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Total Youth Participants
                                </Typography>
                                <Typography variant="h6" fontWeight={900} color={MAIN_THEME_COLOR}>
                                  {getParticipantCountFromRecord(mainRec)}
                                </Typography>
                              </Grid>

                              {isSuperAdmin ? (
                                <Grid item xs={12} sm={1} textAlign="right">
                                  <Tooltip title="Delete Main Registration">
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() =>
                                        setDeleteTarget({
                                          id: mainRec.id || "",
                                          label: `Main Reg - ${group.parishName}`,
                                          collectionName: "dyc_yuva_dabazo",
                                        })
                                      }
                                    >
                                      <DeleteOutline />
                                    </IconButton>
                                  </Tooltip>
                                </Grid>
                              ) : null}

                              <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Selected Events:
                                </Typography>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.5} mt={0.5}>
                                  {(mainRec.selectedEvents || []).map((ev) => (
                                    <Chip
                                      key={ev}
                                      label={eventLabels[ev] || ev}
                                      size="small"
                                      sx={{ fontSize: "0.75rem" }}
                                    />
                                  ))}
                                </Stack>
                              </Grid>

                              {mainRec.events?.spotlight ? (
                                <Grid item xs={12}>
                                  <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: "#f0f7ff", border: `1px solid ${MAIN_THEME_COLOR}22` }}>
                                    <Typography variant="subtitle2" fontWeight={800} color={MAIN_THEME_COLOR}>
                                      SpotLight Details:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700} mt={0.5}>
                                      Event Name / Slot: {mainRec.events.spotlight.performingEventName || mainRec.events.spotlight.entryName || "-"}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" mt={0.5} sx={{ whiteSpace: "pre-wrap" }}>
                                      Event Description: {mainRec.events.spotlight.eventDescription || "-"}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                      Participants: {(mainRec.events.spotlight.participants || []).join(", ") || "-"} | Contact: {mainRec.events.spotlight.teamLeadContact || "-"}
                                    </Typography>
                                  </Box>
                                </Grid>
                              ) : null}
                            </Grid>
                          </Card>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No Main DYC registration recorded for this parish.
                      </Typography>
                    )}

                    {/* Pitch Battle Entries */}
                    {group.pitchBattleRecords.length ? (
                      <Box>
                        <Typography variant="subtitle2" fontWeight={800} color={SECONDARY_THEME_COLOR} mb={1}>
                          Pitch Battle Entries ({group.pitchBattleRecords.length})
                        </Typography>
                        {group.pitchBattleRecords.map((pbRec) => (
                          <Card
                            key={pbRec.id || "pb"}
                            variant="outlined"
                            sx={{ borderRadius: 2, mb: 1.5, p: 2, backgroundColor: "#fffdfa" }}
                          >
                            <Grid container spacing={2} alignItems="flex-start">
                              <Grid item xs={12} sm={4}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Pitch Title
                                </Typography>
                                <Typography variant="subtitle2" fontWeight={800} color={MAIN_THEME_COLOR}>
                                  {pbRec.ideaTitle || "-"}
                                </Typography>
                                <Chip
                                  label={pbRec.entryType === "group" ? "Group" : "Individual"}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  sx={{ mt: 0.5 }}
                                />
                              </Grid>

                              <Grid item xs={12} sm={4}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Participants ({pbRec.participantCount || 1})
                                </Typography>
                                <Typography variant="body2" fontWeight={700}>
                                  {pbRec.participantNames?.join(", ") || "-"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Team Lead Ph: {pbRec.teamLeadContact || "-"}
                                </Typography>
                              </Grid>

                              <Grid item xs={12} sm={3}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  President / Unit Lead
                                </Typography>
                                <Typography variant="body2" fontWeight={700}>
                                  {pbRec.presidentName || "-"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Ph: {pbRec.presidentContactNumber || pbRec.contactNumber || "-"}
                                </Typography>
                              </Grid>

                              {isSuperAdmin ? (
                                <Grid item xs={12} sm={1} textAlign="right">
                                  <Tooltip title="Delete Pitch Battle Entry">
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() =>
                                        setDeleteTarget({
                                          id: pbRec.id || "",
                                          label: pbRec.ideaTitle || "Pitch Battle Entry",
                                          collectionName: "both",
                                        })
                                      }
                                    >
                                      <DeleteOutline />
                                    </IconButton>
                                  </Tooltip>
                                </Grid>
                              ) : null}
                            </Grid>
                          </Card>
                        ))}
                      </Box>
                    ) : null}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ py: 6, textAlign: "center" }}>
                <Typography color="text.secondary">No parish entries found matching query.</Typography>
              </CardContent>
            </Card>
          )}
        </Stack>

        {/* Dialog for Single Record Delete */}
        <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmber color="warning" />
            Delete Registration Entry
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete <strong>{deleteTarget?.label}</strong>? This will permanently erase it from Firestore and Excel sheets.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleDeleteSingleRecord} disabled={isDeleting}>
              {isDeleting ? "Deleting..." : "Delete Permanently"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for Parish Group Delete */}
        <Dialog open={Boolean(parishDeleteTarget)} onClose={() => setParishDeleteTarget(null)}>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: "error.main" }}>
            <WarningAmber color="error" />
            Delete All Entries for {parishDeleteTarget?.parishName}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete <strong>ALL</strong> main registrations and Pitch Battle entries for <strong>{parishDeleteTarget?.parishName}</strong>?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setParishDeleteTarget(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleDeleteParishGroup} disabled={isDeleting}>
              {isDeleting ? "Deleting..." : "Delete All Entries for Parish"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for Clear All DYC Data */}
        <Dialog open={showClearAllDialog} onClose={() => setShowClearAllDialog(false)}>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: "error.main" }}>
            <WarningAmber color="error" />
            Clear All DYC Registration Data
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              <strong>Warning:</strong> This will delete <strong>ALL DYC REGISTRATION DATA</strong> across all parishes. This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setShowClearAllDialog(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleClearAllData} disabled={isDeleting}>
              {isDeleting ? "Clearing All..." : "Clear All DYC Data"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminDashboardLayout>
  );
}
